/**
 * ScrollManager — universal route-aware scroll behaviour.
 *
 * Two distinct behaviours, picked apart by react-router's
 * `useNavigationType()`:
 *
 *   PUSH / REPLACE (forward navigation — guest tapped a product card):
 *     → scroll the destination to y=0 so they land at the headline
 *       they tapped through for. Fixes the classic React Router
 *       gotcha where new pages inherit the previous page's scroll.
 *
 *   POP (browser-back / browser-forward):
 *     → restore the last-known scroll position for the destination
 *       route. So when a guest taps Preview → scrolls → taps Back,
 *       the dashboard reappears EXACTLY where they left it, not at
 *       the top. This is what makes the cinematic continuity feel
 *       alive — guests pick up where they left off, not where the
 *       framework dropped them.
 *
 * IMPLEMENTATION SUBTLETY
 * -----------------------
 * Snapshotting in a pathname-change effect doesn't work — by the time
 * React fires the effect, the route has already mounted the new page
 * and `window.scrollY` is 0. We must snapshot CONTINUOUSLY for the
 * current pathname via a throttled scroll listener, then on pathname
 * change just APPLY the right behaviour (PUSH=top, POP=restore).
 *
 * Snapshots are keyed by `pathname` (search params intentionally
 * dropped — same-page filters typically want fresh-top). Stored in
 * sessionStorage so they survive tab-restore but reset on session end.
 *
 * Opt-out: a destination page that manages its own viewport can
 * declare `<div data-no-scroll-restore="1">…` on its root and we
 * skip the whole apparatus for that route.
 *
 * Mount ONCE at the BrowserRouter level — fires for every Route.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_KEY = 'eventdrop_scroll_snapshots';
const MAX_SNAPSHOTS = 32;
const SCROLL_THROTTLE_MS = 120;

// ── sessionStorage helpers ────────────────────────────────────────────
const readStore = () => {
  if (typeof window === 'undefined') return {};
  try { return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
};

const writeStore = (data) => {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); }
  catch { /* quota / private mode — non-fatal */ }
};

const snapshot = (pathname, y) => {
  const store = readStore();
  // No-op if value is identical — saves disk churn on every scroll.
  if (store[pathname] && store[pathname].y === y) return;
  store[pathname] = { y, t: Date.now() };
  const keys = Object.keys(store);
  if (keys.length > MAX_SNAPSHOTS) {
    keys.sort((a, b) => store[a].t - store[b].t);
    for (let i = 0; i < keys.length - MAX_SNAPSHOTS; i += 1) delete store[keys[i]];
  }
  writeStore(store);
};

const readSnapshot = (pathname) => {
  const v = readStore()[pathname];
  return v && typeof v.y === 'number' ? v.y : null;
};

// ── ScrollManager ─────────────────────────────────────────────────────
export const ScrollManager = () => {
  const { pathname } = useLocation();
  const navType = useNavigationType(); // 'PUSH' | 'REPLACE' | 'POP'
  const currentPathnameRef = useRef(pathname);
  const throttleTimerRef = useRef(null);

  // Keep currentPathnameRef in sync — used by the scroll listener so it
  // always knows which key to write under.
  useEffect(() => { currentPathnameRef.current = pathname; }, [pathname]);

  // Continuous throttled scroll snapshot for the CURRENTLY-mounted
  // route. This is the only reliable way to capture scrollY before a
  // navigation kicks off — by the time pathname changes, scrollY is
  // already 0 on the new page.
  useEffect(() => {
    const onScroll = () => {
      if (throttleTimerRef.current) return;
      throttleTimerRef.current = window.setTimeout(() => {
        throttleTimerRef.current = null;
        snapshot(currentPathnameRef.current, window.scrollY || window.pageYOffset || 0);
      }, SCROLL_THROTTLE_MS);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (throttleTimerRef.current) {
        window.clearTimeout(throttleTimerRef.current);
        throttleTimerRef.current = null;
      }
    };
  }, []);

  // On pathname change → apply the right behaviour. PUSH/REPLACE = top,
  // POP = restore snapshot. Destination pages with async data fetches
  // (EventPage loads photos/stats/spreading) aren't tall enough at
  // first paint — `scrollTo(1800)` would cap at the current content
  // height (~600) and silently leave us at the top. We POLL: attempt
  // the scroll on every animation frame for ~1.5s, stopping early once
  // we land within 4px of target (or the document is finally tall
  // enough to actually accept the value).
  useEffect(() => {
    let cancelled = false;
    const target = navType === 'POP'
      ? (readSnapshot(pathname) ?? 0)
      : 0;

    const applyScroll = (y) => {
      try { window.scrollTo({ top: y, left: 0, behavior: 'instant' }); }
      catch { window.scrollTo(0, y); }
      if (document.documentElement) document.documentElement.scrollTop = y;
      if (document.body) document.body.scrollTop = y;
    };

    const startedAt = Date.now();
    const tryRestore = () => {
      if (cancelled) return;
      const optOut = document.querySelector('[data-no-scroll-restore="1"]');
      if (optOut) return;
      applyScroll(target);
      const actual = window.scrollY || window.pageYOffset || 0;
      // Stop early if we hit the target (within 4px tolerance), OR if
      // we've been polling for >1500ms (give up — content never grew).
      if (Math.abs(actual - target) <= 4 || Date.now() - startedAt > 1500) {
        return;
      }
      window.requestAnimationFrame(tryRestore);
    };
    // Wait one rAF so the destination route has a chance to mount its
    // opt-out flag before we start applying.
    const initial = window.requestAnimationFrame(tryRestore);

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(initial);
    };
  }, [pathname, navType]);

  // Final snapshot on tab close / refresh so a guest who returns lands
  // in the right spot. `pagehide` is more reliable than `beforeunload`
  // on iOS Safari.
  useEffect(() => {
    const onLeave = () => snapshot(currentPathnameRef.current, window.scrollY || 0);
    window.addEventListener('pagehide', onLeave);
    return () => window.removeEventListener('pagehide', onLeave);
  }, []);

  return null;
};

export default ScrollManager;
